import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { signInWithPopup, signOut as firebaseSignOut, getAuth } from 'firebase/auth'
import { getApps, getApp } from 'firebase/app'
import { getFirebaseAuthFor, createGoogleAuthProvider } from '../firebase/firebaseConfig'
import { httpClient, setSessionTokenProvider } from '../api/httpClient'

/**
 * Port of `app/register/authService.js` (AngularJS `AuthService`) to a
 * React context + `useAuth()` hook, extended to support Firebase/Google
 * OAuth as the primary login flow (specs/react-migration/
 * 009-auth-context-firebase.md), while keeping the legacy
 * `POST /users/login` flow working (`login()` below) since nothing in this
 * worktree has removed it from the backend yet.
 *
 * Session persistence: the legacy service stored 5 separate cookies
 * (`sessionId`, `userId`, `userName`, `userLogin`, `urlAstrea`), each
 * expiring 1 day (`today.setDate(today.getDate() + 1)`) after being set.
 * `urlAstrea` is no longer part of the session here — it stopped being a
 * per-user preference (see `shared/hooks/useAstreaEnvironment.js`, which
 * reads it as fixed app config instead). The remaining 4 fields are
 * persisted as a single JSON object in
 * `localStorage` (key `SESSION_STORAGE_KEY`) alongside an explicit
 * `expiresAt` timestamp computed the same way (+1 day), so a page refresh
 * restores the session (read on mount, see `useEffect` below) and an
 * expired session is treated as logged-out without a network round-trip.
 * `localStorage` was chosen over cookies because nothing server-side reads
 * these values directly anymore for the Firebase flow (the ID token is
 * sent explicitly as a header — see `httpClient.js` — not as a cookie);
 * `sessionStorage` was not used because the legacy behavior explicitly
 * intended sessions to survive across browser restarts for 1 day, which
 * `sessionStorage` does not support.
 */

const SESSION_STORAGE_KEY = 'astrea.auth.session'
const SESSION_TTL_MS = 24 * 60 * 60 * 1000 // 1 day, same rule as authService.js

const AuthContext = createContext(undefined)

function getExpiryTimestamp() {
  return Date.now() + SESSION_TTL_MS
}

/** Reads and validates the persisted session, clearing it if expired/corrupt. */
function readStoredSession() {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null

    const session = JSON.parse(raw)
    if (!session || !session.expiresAt || Date.now() >= session.expiresAt) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY)
      return null
    }

    return session
  } catch {
    window.localStorage.removeItem(SESSION_STORAGE_KEY)
    return null
  }
}

function writeStoredSession(session) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

function clearStoredSession() {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(SESSION_STORAGE_KEY)
}

/**
 * Normalizes a `server_app` auth response (`POST /users/login` or
 * `POST /users/firebase-login`, both return the same shape — see
 * `server_app/routes/userRouter.js`) plus the token used to authenticate
 * subsequent requests into the session object persisted here.
 */
function toSession(userResponse, token) {
  return {
    token,
    sessionId: userResponse.sessionId,
    userId: userResponse.userId,
    userName: userResponse.userName,
    userLogin: userResponse.userLogin,
    expiresAt: getExpiryTimestamp(),
  }
}

// `prop-types` isn't a project dependency (see the same note in
// `AppProviders.jsx`), so the `react/prop-types` lint rule is disabled here.
// eslint-disable-next-line react/prop-types
export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readStoredSession())

  // Equivalent of the legacy request interceptor forwarding the session
  // cookie (`httpInterceptor.js` + task 008's `X-Session-Id` mirror): give
  // `httpClient` a getter for the *current* token so every request (login,
  // Firebase or legacy, and any request made after a subsequent login/
  // logout) always carries the freshest value, without `httpClient`
  // depending on React.
  useEffect(() => {
    setSessionTokenProvider(() => session?.token)
    return () => setSessionTokenProvider(null)
  }, [session])

  /** Legacy login flow: `POST /users/login` with `{ username, password }`. */
  const login = useCallback(async (credentials) => {
    const response = await httpClient.post('/users/login', credentials)
    const user = response.data
    const nextSession = toSession(user, user.sessionId)
    writeStoredSession(nextSession)
    setSession(nextSession)
    return user
  }, [])

  /**
   * Primary login flow, ported from `AuthService.loginWithGoogle()`
   * (commit a6c16fa): fetches the per-environment Firebase config
   * (`GET /users/firebase-config`), opens the Google OAuth popup restricted
   * to the allowed Workspace domain, obtains an ID token from the Firebase
   * Auth SDK, and sends it to the backend (`POST /users/firebase-login`)
   * for JWKS validation (`sign_in_provider`/domain checks) + session/user
   * provisioning (`server_app/routes/userRouter.js`). The Firebase ID token
   * itself (not a legacy `sessionId`) becomes the token attached to
   * subsequent requests via `X-Session-Id` (see `httpClient.js`).
   */
  const loginWithGoogle = useCallback(async () => {
    const configResponse = await httpClient.get('/users/firebase-config')
    const firebaseConfig = configResponse.data

    const auth = getFirebaseAuthFor(firebaseConfig)
    const provider = createGoogleAuthProvider(firebaseConfig.allowedDomain)
    const credential = await signInWithPopup(auth, provider)
    const idToken = await credential.user.getIdToken()

    const response = await httpClient.post('/users/firebase-login', { idToken })
    const user = response.data
    const nextSession = toSession(user, idToken)
    writeStoredSession(nextSession)
    setSession(nextSession)
    return user
  }, [])

  /**
   * Equivalent of `authService.logout()`'s 5 `$cookies.remove(...)` calls —
   * clears the entire persisted session (one `localStorage` entry here
   * instead of 5 cookies) and, if the current session came from Firebase,
   * also signs out of the Firebase SDK so a subsequent Google popup
   * doesn't silently reuse the previous account without a chooser.
   */
  const logout = useCallback(() => {
    clearStoredSession()
    setSession(null)
    // Only attempt Firebase sign-out if `loginWithGoogle()` actually
    // initialized an app this session (legacy password login never does) —
    // calling `getApp()`/`getAuth()` without one would throw instead of
    // resolving to "nothing to sign out of".
    if (getApps().length) {
      firebaseSignOut(getAuth(getApp())).catch(() => {
        // Sign-out failed for some other reason — local session state
        // above is already cleared, so there's nothing more to do.
      })
    }
  }, [])

  const isAuthenticated = useCallback(() => !!session?.userId, [session])
  const getUserLogin = useCallback(() => session?.userLogin, [session])
  const getUserName = useCallback(() => session?.userName, [session])

  const value = useMemo(
    () => ({
      session,
      login,
      loginWithGoogle,
      logout,
      isAuthenticated,
      getUserLogin,
      getUserName,
    }),
    [session, login, loginWithGoogle, logout, isAuthenticated, getUserLogin, getUserName],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth() must be used within an <AuthProvider>.')
  }
  return context
}

export default AuthContext
